import type { AccountListItem } from '@/client/types.gen'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import type { RowSelectionState, Table, Updater } from '@tanstack/react-table'
import React, { useCallback, useEffect, useState } from 'react'
import { useAnalytics } from '@/hooks/use-analytics'
import { useAutoTour } from '@/hooks/use-tour'
import { useIsMobile } from '@/hooks/use-mobile'
import { celebrate } from '@/lib/celebrate'
import { countActiveAccountsFilters } from '@/lib/analytics/accounts'
import { AccountsPageSkeleton } from './-components/skeleton'
import type { ViewportBounds } from './-utils/table-context'
import { TableContext } from './-utils/table-context'
import { AccountsPanel } from './-components/accounts-panel'
import { MapAccountsDisplayControls } from './-components/toolbar/accounts-display-controls'
import { SelectionToolbar } from './-components/toolbar/selection-toolbar'
import { AccountsMapView } from './-components/map/accounts-map-view-mvt'
import { AccountsTableView } from './-components/table/accounts-table-view'
import { GroupedAccountsTableView } from './-components/table/grouped-accounts-table-view'
import { AccountsHeatMapView } from './-components/heat-map/accounts-heat-map-view'
import { useHeatMapEnabled } from './-utils/heat-map'
import type { AccountsView } from './-utils/view-persistence'
import { getAccountsLastView, parseAccountsView } from './-utils/view-persistence'
import { AccountsFilterProvider, useAccountsFilterState } from './-components/filters/use-accounts-filter-state'
import type { AccountsViewMode } from './-components/toolbar/view-mode-types'
import { getPersistedViewMode } from './-components/toolbar/view-mode-types'

interface AccountsSearch {
  view?: AccountsView
  from?: string
}

function AccountsAnalyticsMount({
  view,
  tableMode,
  fromOnboarding,
}: {
  view: AccountsView
  tableMode: AccountsViewMode
  fromOnboarding: boolean
}) {
  const { accountsFilterState } = useAccountsFilterState()
  const { capture } = useAnalytics()
  const viewedRef = React.useRef(false)

  useEffect(() => {
    if (viewedRef.current)
      return
    viewedRef.current = true
    capture('accounts_viewed', {
      view,
      table_mode: view === 'table' ? tableMode : undefined,
      active_filter_count: countActiveAccountsFilters(accountsFilterState),
      has_search: !!accountsFilterState.searchTerm?.trim(),
      from_onboarding: fromOnboarding,
    })
  }, [accountsFilterState, capture, fromOnboarding, tableMode, view])

  return <></>
}

function AccountsIndexPage() {
  useAutoTour('accounts-intro')
  const isMobile = useIsMobile()
  const [table, setTable] = useState<Table<AccountListItem> | undefined>()
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [selectedRows, setSelectedRows] = useState<AccountListItem[]>([])
  const [selectAllMode, setSelectAllMode] = useState(false)
  const [totalFilteredCount, setTotalFilteredCount] = useState<number | undefined>()
  const [mapBounds, setMapBounds] = useState<ViewportBounds | undefined>()
  const [viewportAccountCount, setViewportAccountCount] = useState<number | undefined>()
  const [viewMode, setViewMode] = useState<AccountsViewMode>(getPersistedViewMode)
  const { view, from } = useSearch({ from: '/_authed/_user/accounts/' })
  const heatMapEnabled = useHeatMapEnabled()
  // A saved/linked heat map view falls back to the table when the flag is off.
  const displayView = view === 'heatmap' && !heatMapEnabled ? 'table' : (view ?? 'table')
  const effectiveViewMode: AccountsViewMode = isMobile ? 'list' : viewMode
  const navigate = useNavigate()
  const { capture } = useAnalytics()

  // Celebrate the partner-onboarding reveal on arrival, then strip the query param
  // so a back/forward navigation doesn't re-fire the effect.
  useEffect(() => {
    if (from !== 'onboarding')
      return
    celebrate()
    capture('onboarding_completed', { landedOn: 'accounts' })
    capture('shared_accounts_viewed', { trigger: 'onboarding_reveal' })
    void navigate({
      to: '/accounts',
      search: view ? { view } : {},
      replace: true,
    })
  }, [capture, from, view, navigate])

  // Clear map bounds when switching away from map view
  const previousView = React.useRef(displayView)
  React.useEffect(() => {
    if (previousView.current === 'map' && displayView !== 'map') {
      setMapBounds(undefined)
      setViewportAccountCount(undefined)
    }
    previousView.current = displayView
  }, [displayView])

  // Handle row selection updates (supports both value and updater function)
  const handleSetRowSelection = useCallback((updaterOrValue: Updater<RowSelectionState>) => {
    setRowSelection(previous =>
      typeof updaterOrValue === 'function' ? updaterOrValue(previous) : updaterOrValue,
    )
    // Clear select-all mode when manually changing selection
    setSelectAllMode(false)
  }, [])

  return (
    <AccountsFilterProvider>
      <TableContext.Provider value={{
        table,
        setTable,
        rowSelection,
        setRowSelection: handleSetRowSelection,
        selectedRows,
        setSelectedRows,
        selectAllMode,
        setSelectAllMode,
        totalFilteredCount,
        setTotalFilteredCount,
        mapBounds,
        setMapBounds,
        viewportAccountCount,
        setViewportAccountCount,
        viewMode: effectiveViewMode,
        setViewMode,
      }}
      >
        <AccountsAnalyticsMount
          view={displayView}
          tableMode={effectiveViewMode}
          fromOnboarding={from === 'onboarding'}
        />
        <React.Suspense fallback={<AccountsPageSkeleton />}>
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden px-4 py-4 lg:px-6 md:py-6">
            <div data-tour="accounts-list" className="flex min-w-0 flex-1 flex-col overflow-hidden">
              {displayView === 'heatmap' && (
                <AccountsPanel contentClassName="flex flex-col overflow-hidden">
                  <AccountsHeatMapView />
                </AccountsPanel>
              )}
              {displayView === 'map' && (
                <AccountsPanel
                  contentClassName="flex flex-col overflow-hidden"
                  displayControls={<MapAccountsDisplayControls />}
                >
                  <AccountsMapView />
                </AccountsPanel>
              )}
              {displayView === 'table' && (effectiveViewMode === 'grouped'
                ? <GroupedAccountsTableView />
                : <AccountsTableView />)}
            </div>
          </div>
          <SelectionToolbar key={selectedRows.length > 0 || selectAllMode ? 'active' : 'inactive'} />
        </React.Suspense>
      </TableContext.Provider>
    </AccountsFilterProvider>
  )
}

export const Route = createFileRoute('/_authed/_user/accounts/')({
  validateSearch: (search: Record<string, unknown>): AccountsSearch => {
    const raw = search as AccountsSearch
    const savedView = raw.view ?? getAccountsLastView()
    const out: AccountsSearch = {}
    if (savedView)
      out.view = parseAccountsView(savedView)
    if (raw.from)
      out.from = raw.from
    return out
  },
  component: AccountsIndexPage,
  pendingComponent: AccountsPageSkeleton,
})
